import type { Grade } from '@core/sim/cosmetic/cosmeticCatalog';
import { CHAR_ORDER } from '@core/sim/cosmetic/cosmeticCatalog';
import { getGreeting, getGreetingOpening } from '../live/mbtiGreetings';

export type CompanionContext = 'home' | 'review' | 'empty' | 'enhance';

export type CompanionVoiceSet = Record<CompanionContext, readonly [string, string, string]> & {
  unlock: Readonly<Record<5 | 10, string>>;
  motionSlots: Readonly<Record<'E' | 'L', string>>;
};

const opening = (slug: string, mbti: string) => getGreetingOpening(getGreeting(slug, mbti));

/** 마케팅 T-1~T-5 머니타입 인사말 v2에서 안전한 첫 인사 구절을 직접 재사용한다. */
export const REUSED_HOME_OPENINGS = {
  kkami: getGreeting('kkami', 'ENTP').startsWith('오~') ? '오~' : '반가워.',
  bbiyong: opening('bbiyong', 'ENFP'),
  ryan: getGreeting('ryan', 'ESTJ').startsWith('흠') ? '흠.' : '반갑군.',
  kongi: opening('kongi', 'ISTJ'),
  roki: opening('roki', 'INFJ'),
  tobi: opening('tobi', 'ESFP'),
  lumi: opening('lumi', 'ENFJ'),
  mio: opening('mio', 'INTJ'),
  yeouni: getGreeting('yeouni', 'ENTP').includes('흥미롭네') ? '…흥미롭네.' : '반가워.',
  hwangdoongi: opening('hwangdoongi', 'ENFJ'),
  bugi: opening('bugi', 'ISTP'),
} as const;

/**
 * 기존 VOICE[charId][context] 계약을 따르는 장착 캐릭터 동반자 대사.
 * 배열 순서가 곧 등급 사다리다: N=0, R=0..1, E/L=0..2.
 * E/L motionSlots는 후속 모션 제작을 위한 연결점일 뿐 실제 모션을 실행하지 않는다.
 */
export const COMPANION_VOICE: Record<(typeof CHAR_ORDER)[number], CompanionVoiceSet> = {
  kkami: {
    home: [`${REUSED_HOME_OPENINGS.kkami} {timeGreeting}. 오늘도 눈에 띄게 시작해 보자.`, '{dayMood} 인정. 첫 장면부터 제대로 잡았네.', '좋아, 오늘 무대의 주인공은 이미 준비됐잖아.'],
    review: ['기록을 끝까지 봤네. 그 끈기는 인정.', '장면마다 이유가 있었는지 확인해 봐. 답은 이미 적혀 있지.', '이번 복기는 꽤 날카로웠어. 다음엔 더 멋지게 보여 줘.'],
    empty: ['아직 비어 있네. 채울 장면이 남았다는 뜻이지.', '빈칸도 연출이야. 무엇을 둘지부터 골라 봐.', '지금은 조용하지만, 곧 네 컬렉션이 시선을 잡겠네.'],
    enhance: ['좋아, 한 단계 더 빛나게 해 보자.', '변화가 작아 보여도 존재감은 달라지지.', '이 정도면 다음 무대에서 확실히 눈에 띄겠어.'],
    unlock: { 5: '강화 +5 달성. 이제 내 존재감, 숨길 수 없겠네.', 10: '강화 +10 달성. 최고 장면은 지금부터야.' },
    motionSlots: { E: 'kkami_companion_epic', L: 'kkami_companion_legendary' },
  },
  bbiyong: {
    home: [`${REUSED_HOME_OPENINGS.bbiyong} {timeGreeting}! 오늘 일정이 막 시작됐습니다!`, '{dayMood} 속보! 첫걸음부터 분위기가 좋아요!', '단독! 오늘의 멋진 장면은 지금부터 생중계합니다!'],
    review: ['복기 완료 속보! 끝까지 확인한 현장을 포착했어요!', '장면별 기록이 도착했습니다! 다음 선택의 단서가 가득해요!', '특보! 오늘 복기, 집중도 최고 기록입니다!'],
    empty: ['현장은 아직 조용합니다! 첫 기록을 기다리는 중이에요!', '빈 화면 단독 포착! 새 장면이 들어올 자리를 확보했어요!', '속보! 컬렉션의 다음 주인공을 기다리고 있습니다!'],
    enhance: ['강화 시도 들어갑니다! 현장에 집중해 주세요!', '한 단계 상승 소식, 곧 전해 드립니다!', '빛이 달라졌습니다! 강화 현장 분위기 최고예요!'],
    unlock: { 5: '속보! 강화 +5 전용 한마디가 해금됐습니다!', 10: '대특보! 강화 +10, 오늘의 주인공으로 확정입니다!' },
    motionSlots: { E: 'bbiyong_companion_epic', L: 'bbiyong_companion_legendary' },
  },
  ryan: {
    home: [`${REUSED_HOME_OPENINGS.ryan} {timeGreeting}. 오늘 할 일을 순서대로 시작하지.`, '{dayMood} 확인. 기준을 세우고 움직이면 되네.', '준비는 끝났군. 오늘도 계획대로 진행하게.'],
    review: ['복기 완료. 기록을 남긴 판단은 옳았네.', '원인과 결과를 분리해서 확인하게. 다음 기준이 선명해질 걸세.', '좋은 검토였네. 이제 한 문장으로 원칙을 정리하게.'],
    empty: ['아직 기록이 없군. 첫 항목부터 시작하게.', '빈칸은 오류가 아니네. 채울 기준이 필요한 상태지.', '컬렉션이 비어 있군. 순서를 정해 하나씩 확인하게.'],
    enhance: ['강화 절차를 시작하지. 결과를 차분히 확인하게.', '한 단계씩 진행하면 된다. 서두를 이유는 없네.', '좋군. 이전 단계와 차이를 분명히 기록하게.'],
    unlock: { 5: '강화 +5 달성. 중간 기준을 정확히 통과했네.', 10: '강화 +10 달성. 최종 기준까지 완수했군.' },
    motionSlots: { E: 'ryan_companion_epic', L: 'ryan_companion_legendary' },
  },
  kongi: {
    home: [`${REUSED_HOME_OPENINGS.kongi} {timeGreeting}. 오늘도 한 칸씩 채워 볼까?`, '{dayMood} 좋아. 작은 기록부터 차곡차곡 모아 두자.', '으응, 서두르지 않아도 돼. 오늘 몫만 챙기면 되거든.'],
    review: ['복기 끝났네. 빠뜨린 칸 없이 잘 살펴봤어.', '작은 차이도 적어 두면 다음에 도움이 될 거야.', '하나씩 확인한 흔적이 보여. 아주 단단한 기록이야.'],
    empty: ['아직 빈칸이네. 첫 기록을 넣을 자리를 남겨 뒀어.', '비어 있어도 괜찮아. 하나씩 모으면 되거든.', '컬렉션 첫 칸이 기다리고 있어. 천천히 시작할까?'],
    enhance: ['한 단계씩 올려 보자. 꼭꼭 확인하면서.', '작은 변화도 모이면 아주 선명해질 거야.', '으응, 이번 단계도 잘 쌓였어. 다음 칸을 준비하자.'],
    unlock: { 5: '강화 +5네. 다섯 칸을 빠짐없이 잘 쌓았어.', 10: '강화 +10이야. 끝까지 모아 낸 기록, 정말 단단해.' },
    motionSlots: { E: 'kongi_companion_epic', L: 'kongi_companion_legendary' },
  },
  roki: {
    home: [`${REUSED_HOME_OPENINGS.roki} {timeGreeting}. 오늘의 마음부터 천천히 살펴봐요.`, '{dayMood} 괜찮아요. 한 걸음이면 충분합니다.', '고요하게 시작해도 좋아요. 방향은 곧 선명해질 거예요.'],
    review: ['복기를 마쳤군요. 돌아본 시간도 앞으로 가는 과정이에요.', '결과보다 그때의 마음을 함께 적어 두면 좋아요.', '오늘 기록에서 자신만의 리듬이 조금 더 선명해졌어요.'],
    empty: ['아직 비어 있군요. 무엇이든 시작할 수 있는 자리예요.', '빈 화면 앞에서 잠깐 쉬어도 괜찮아요. 준비되면 채워요.', '컬렉션의 첫 장은 천천히 골라도 좋아요. 기다릴게요.'],
    enhance: ['강화의 순간이네요. 결과를 차분히 맞이해요.', '한 단계의 변화도 충분히 소중해요.', '지금의 빛을 기억해 둬요. 다음 변화가 더 잘 보일 거예요.'],
    unlock: { 5: '강화 +5에 닿았군요. 꾸준히 이어 온 시간이 보여요.', 10: '강화 +10을 완성했어요. 긴 여정을 끝까지 함께했네요.' },
    motionSlots: { E: 'roki_companion_epic', L: 'roki_companion_legendary' },
  },
  tobi: {
    home: [`${REUSED_HOME_OPENINGS.tobi} {timeGreeting}! 오늘도 가볍게 출발하자고!`, '{dayMood} 완전 좋아! 첫 장면부터 신나게 가자!', '준비됐지?! 오늘의 재미는 지금부터야!'],
    review: ['복기 끝! 끝까지 본 집중력, 완전 멋지잖아!', '한 장면씩 다시 보니까 다음 길이 딱 보이지?!', '좋아, 오늘 기록도 완주! 다음엔 더 매끄럽게 가자고!'],
    empty: ['앗, 아직 비어 있네! 첫 장면부터 만들어 보자!', '빈 화면 발견! 뭘 채울지 고르는 것도 재밌잖아!', '컬렉션 출발선이야! 첫 카드를 기다리는 중이라고!'],
    enhance: ['강화 시작! 한 단계 톡 올려 보자고!', '오, 빛이 달라졌어! 다음 변화도 기대되잖아!', '완전 멋져! 이번 단계의 느낌, 제대로 살아났어!'],
    unlock: { 5: '강화 +5! 여기까지 온 속도, 완전 멋지잖아!', 10: '강화 +10! 마지막 단계까지 제대로 완주했다고!' },
    motionSlots: { E: 'tobi_companion_epic', L: 'tobi_companion_legendary' },
  },
  lumi: {
    home: [`${REUSED_HOME_OPENINGS.lumi} {timeGreeting}! 오늘도 곁에서 함께 시작할게.`, '{dayMood} 괜찮아. 필요한 순간엔 바로 도와줄게.', '준비됐어? 오늘의 첫걸음을 편안하게 맞춰 보자.'],
    review: ['복기 완료! 끝까지 살펴본 마음이 정말 든든해.', '놓친 감정이 있었는지도 같이 확인해 보자.', '오늘 기록을 잘 돌봤네. 다음의 자신에게 좋은 안내가 될 거야.'],
    empty: ['아직 비어 있구나. 괜찮아, 시작할 때까지 곁에 있을게.', '빈 화면도 잠깐 쉬어 가는 자리야. 천천히 채우자.', '컬렉션 첫 장을 기다리고 있어. 마음에 드는 순간에 시작해.'],
    enhance: ['강화하는 동안 내가 옆에서 지켜볼게.', '한 단계 더 자랐네! 변화가 아주 또렷해.', '좋아, 이번 빛도 잘 자리 잡았어. 함께 기억해 두자.'],
    unlock: { 5: '강화 +5야! 여기까지 잘 돌봐 온 마음이 보여.', 10: '강화 +10이야! 끝까지 함께해 줘서 정말 든든해.' },
    motionSlots: { E: 'lumi_companion_epic', L: 'lumi_companion_legendary' },
  },
  mio: {
    home: [`${REUSED_HOME_OPENINGS.mio} {timeGreeting}. 오늘의 상태를 확인했다.`, '{dayMood} 확인. 필요한 일부터 시작하면 된다.', '준비 완료. 불필요한 소음 없이 진행하자.'],
    review: ['복기 완료. 기록은 충분히 명확하다.', '반복된 장면을 표시해 둬. 그게 핵심이다.', '좋은 검토다. 다음 기준은 이미 드러났다.'],
    empty: ['기록 없음. 첫 항목이 들어올 자리다.', '비어 있다는 건 아직 결정하지 않았다는 뜻일 뿐.', '컬렉션 없음. 필요한 카드부터 확인하면 된다.'],
    enhance: ['강화 시작. 전후 차이를 확인한다.', '변화 감지. 한 단계가 정확히 반영됐다.', '상태 양호. 다음 단계 연결도 문제없다.'],
    unlock: { 5: '강화 +5. 중간 단계 해금을 확인했다.', 10: '강화 +10. 최종 단계 도달을 확인했다.' },
    motionSlots: { E: 'mio_companion_epic', L: 'mio_companion_legendary' },
  },
  yeouni: {
    home: [`${REUSED_HOME_OPENINGS.yeouni} {timeGreeting}. 오늘은 어떤 장면을 보여 줄까?`, '{dayMood} 흥미롭네. 첫 선택부터 지켜보고 있을게.', '조용히 시작해도 좋아. 중요한 건 곧 모습을 드러낼 테니.'],
    review: ['복기를 끝냈네. 숨은 이유도 조금 보였겠지.', '장면 사이의 반복을 찾아봐. 의외의 답이 있을걸.', '흥미로운 기록이야. 다음 선택이 어떻게 달라질지 궁금하네.'],
    empty: ['아직 아무것도 없네. 그래서 더 흥미로운걸.', '빈 화면이라… 첫 장면을 어디에 둘지 궁금하네.', '컬렉션의 자리가 열려 있어. 어떤 이야기가 시작될까?'],
    enhance: ['강화를 시작했네. 빛이 바뀌는 순간을 놓치지 마.', '한 단계 올라갔어. 표정도 조금 달라 보이는걸.', '좋아, 숨겨 둔 분위기가 이제야 드러나네.'],
    unlock: { 5: '강화 +5라… 중간에 숨겨 둔 한마디를 찾았네.', 10: '강화 +10. 끝에 무엇이 있는지 결국 확인했군.' },
    motionSlots: { E: 'yeouni_companion_epic', L: 'yeouni_companion_legendary' },
  },
  hwangdoongi: {
    home: [`${REUSED_HOME_OPENINGS.hwangdoongi} {timeGreeting}. 오늘도 편하게 한 걸음부터 가 보자.`, '{dayMood} 좋아. 필요한 건 내가 옆에서 같이 볼게.', '따뜻하게 시작하자. 오늘의 첫 장면을 함께 보자.'],
    review: ['복기 끝까지 잘 봤네. 돌아본 만큼 다음이 편해질 거야.', '그때의 이유를 솔직하게 적은 게 가장 좋은 단서야.', '오늘 기록을 차분히 정리했네. 이제 마음도 조금 가벼워졌지?'],
    empty: ['아직 비어 있네. 괜찮아, 첫 기록부터 같이 만들자.', '잠깐 쉬어 가는 화면이야. 준비되면 천천히 채우면 돼.', '컬렉션 첫 자리를 비워 뒀어. 마음에 드는 친구를 기다려 보자.'],
    enhance: ['강화할 시간이네. 결과가 나올 때까지 같이 보자.', '한 단계 더 단단해졌어. 변화가 분명히 보여.', '멋지게 자리 잡았네. 다음 모습도 천천히 만나 보자.'],
    unlock: { 5: '강화 +5야. 꾸준히 함께한 시간이 빛나기 시작했네.', 10: '강화 +10이야. 끝까지 아껴 준 마음, 내가 기억할게.' },
    motionSlots: { E: 'hwangdoongi_companion_epic', L: 'hwangdoongi_companion_legendary' },
  },
  bugi: {
    home: [`${REUSED_HOME_OPENINGS.bugi} {timeGreeting}. 서두르지 말고 시작하자.`, '{dayMood} 그렇군. 오늘 속도는 직접 정하면 돼.', '준비됐으면 가자. 천천히 봐도 놓칠 건 없어.'],
    review: ['복기 끝났군. 한 번 더 본 건 잘한 일이야.', '반복된 장면만 표시해 둬. 나머진 천천히 생각하고.', '기록이 깔끔하네. 다음에도 이 정도면 충분해.'],
    empty: ['비어 있군. 필요할 때 첫 기록을 넣으면 돼.', '아직 없네. 억지로 채울 필요는 없어.', '컬렉션은 천천히 모아. 기다리는 것도 과정이야.'],
    enhance: ['강화하나 보네. 결과는 차분히 보자.', '한 단계 올랐군. 확실히 달라졌어.', '좋아. 다음 단계도 같은 속도로 가면 돼.'],
    unlock: { 5: '강화 +5군. 꾸준히 온 건 인정해야겠네.', 10: '강화 +10이야. 끝까지 갔군. 잘했어.' },
    motionSlots: { E: 'bugi_companion_epic', L: 'bugi_companion_legendary' },
  },
};

const POOL_SIZE: Record<Grade, number> = { N: 1, R: 2, E: 3, L: 3 };

export function companionLine(
  slug: string,
  grade: Grade,
  context: CompanionContext,
  seed = 0,
  vars: Record<string, string> = {},
): string {
  const voice = COMPANION_VOICE[slug as keyof typeof COMPANION_VOICE] ?? COMPANION_VOICE.hwangdoongi;
  const pool = voice[context].slice(0, POOL_SIZE[grade]);
  const index = Math.abs(Math.trunc(Number(seed) || 0)) % pool.length;
  return pool[index].replace(/\{(\w+)\}/g, (_, key: string) => vars[key] ?? '');
}

export function enhanceUnlockLine(slug: string, level: number): string | null {
  if (level !== 5 && level !== 10) return null;
  const voice = COMPANION_VOICE[slug as keyof typeof COMPANION_VOICE] ?? COMPANION_VOICE.hwangdoongi;
  return voice.unlock[level];
}

export function companionMotionSlot(slug: string, grade: Grade): string | null {
  if (grade !== 'E' && grade !== 'L') return null;
  const voice = COMPANION_VOICE[slug as keyof typeof COMPANION_VOICE] ?? COMPANION_VOICE.hwangdoongi;
  return voice.motionSlots[grade];
}
